import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { withRealPrefix } from '../utils/siteMode';
import ProductShowcase from './ProductShowcase';

function Profile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateUserInfo, updatePassword, bindEmail, logout } = useAuth();
  const fileInputRef = useRef(null);
  
  const [editing, setEditing] = useState(false);
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [emailLoading, setEmailLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // 未登录跳转
  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <ProductShowcase />
        <div style={styles.contentLayer}>
          <div style={styles.card}>
            <h2 style={styles.title}>请先登录</h2>
            <p style={styles.subtitle}>登录后可查看和编辑个人信息</p>
            <button 
              onClick={() => navigate(withRealPrefix('/login'))} 
              style={styles.primaryButton}
            >
              去登录
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 选择头像文件
  const handleAvatarClick = () => {
    if (editing) {
      fileInputRef.current?.click();
    }
  };

  // 处理文件选择
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: '图片大小不能超过 2MB' });
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 保存修改
  const handleSave = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // 如果有新头像，先上传头像
      let avatarUrl = user?.avatar;
      
      if (avatarFile) {
        // 将图片转为 base64 发送（简单方案）
        // 实际生产环境建议使用专门的图片上传服务
        avatarUrl = avatarPreview;
      }

      const result = await updateUserInfo({
        nickname: nickname || user?.nickname,
        avatar: avatarUrl
      });

      if (result.success) {
        setMessage({ type: 'success', text: '保存成功！' });
        setEditing(false);
        setAvatarFile(null);
      } else {
        setMessage({ type: 'error', text: result.error || '保存失败' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }

    setLoading(false);
  };

  // 取消编辑
  const handleCancel = () => {
    setEditing(false);
    setNickname(user?.nickname || '');
    setAvatarPreview(null);
    setAvatarFile(null);
    setEmail(user?.email || '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setMessage({ type: '', text: '' });
  };

  // 设置/修改登录密码
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!newPassword) {
      setMessage({ type: 'error', text: '请输入新密码' });
      return;
    }
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: '新密码至少 6 位' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: '两次新密码输入不一致' });
      return;
    }
    const shouldRequireCurrentPassword = !user?.openid && !!user?.hasPassword;
    if (shouldRequireCurrentPassword && !currentPassword) {
      setMessage({ type: 'error', text: '请输入当前密码' });
      return;
    }

    setPasswordLoading(true);
    const result = await updatePassword({
      currentPassword: shouldRequireCurrentPassword ? currentPassword : undefined,
      newPassword
    });
    setPasswordLoading(false);

    if (result.success) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage({ type: 'success', text: result.message || '密码更新成功' });
    } else {
      setMessage({ type: 'error', text: result.error || '密码更新失败' });
    }
  };

  // 绑定邮箱
  const handleBindEmail = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const normalized = (email || '').trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!normalized) {
      setMessage({ type: 'error', text: '请输入邮箱地址' });
      return;
    }
    if (!emailRegex.test(normalized)) {
      setMessage({ type: 'error', text: '邮箱格式不正确' });
      return;
    }

    setEmailLoading(true);
    const result = await bindEmail(normalized);
    setEmailLoading(false);

    if (result.success) {
      setEmail(normalized);
      setMessage({ type: 'success', text: result.message || '邮箱绑定成功' });
    } else {
      setMessage({ type: 'error', text: result.error || '邮箱绑定失败' });
    }
  };

  // 退出登录
  const handleLogout = () => {
    logout();
    navigate(withRealPrefix('/Home'));
  };

  const currentAvatar = avatarPreview || user?.avatar || user?.figureurl_qq_2 || user?.figureurl_2;
  const shouldRequireCurrentPassword = !user?.openid && !!user?.hasPassword;

  return (
    <div style={styles.container}>
      {/* 产品展示背景 */}
      <div style={{ opacity: 0.3 }}>
        <ProductShowcase />
      </div>

      {/* 内容层 */}
      <div style={styles.contentLayer}>
        <div style={styles.card}>
          {/* 关闭按钮 - 返回上一页 */}
          <button 
            onClick={() => editing ? handleCancel() : navigate(-1)}
            style={styles.closeButton}
            title={editing ? "取消编辑" : "返回"}
          >
            ✕
          </button>
          
          <h1 style={styles.title}>{editing ? '编辑资料' : '个人中心'}</h1>

          {/* 消息提示 */}
          {message.text && (
            <div style={{
              ...styles.messageBox,
              backgroundColor: message.type === 'success' 
                ? 'rgba(16, 185, 129, 0.1)' 
                : 'rgba(239, 68, 68, 0.1)',
              borderColor: message.type === 'success' 
                ? 'rgba(16, 185, 129, 0.3)' 
                : 'rgba(239, 68, 68, 0.3)',
              color: message.type === 'success' ? '#10b981' : '#ef4444'
            }}>
              {message.text}
            </div>
          )}

          {/* 头像区域 */}
          <div style={styles.avatarSection}>
            <div 
              style={{
                ...styles.avatarWrapper,
                cursor: editing ? 'pointer' : 'default',
              }}
              onClick={handleAvatarClick}
            >
              {currentAvatar ? (
                <img 
                  src={currentAvatar} 
                  alt="头像" 
                  style={styles.avatar}
                />
              ) : (
                <div style={styles.avatarPlaceholder}>
                  {(user?.nickname || user?.username || '用户')[0]?.toUpperCase()}
                </div>
              )}
              
              {editing && (
                <div style={styles.avatarOverlay}>
                  <span>📷</span>
                  <span style={{ fontSize: '12px' }}>更换头像</span>
                </div>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {editing && (
              <p style={styles.avatarHint}>点击头像更换，支持 JPG、PNG，最大 2MB</p>
            )}
          </div>

          {/* 用户信息 */}
          <div style={styles.infoSection}>
            {/* 昵称 */}
            <div style={styles.infoItem}>
              <label style={styles.label}>昵称</label>
              {editing ? (
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="请输入昵称"
                  style={styles.input}
                  maxLength={20}
                />
              ) : (
                <div style={styles.value}>{user?.nickname || '未设置'}</div>
              )}
            </div>

            {/* 账号 */}
            <div style={styles.infoItem}>
              <label style={styles.label}>账号</label>
              <div style={styles.value}>{user?.username || user?.email || '-'}</div>
            </div>

            <div style={styles.emailBlock}>
              <div style={styles.passwordTitle}>
                {user?.email ? '修改绑定邮箱' : '绑定邮箱'}
              </div>
              <p style={styles.passwordHint}>
                {user?.email ? `当前邮箱：${user.email}` : '绑定邮箱后可用于账号密码登录与找回。'}
              </p>
              <form onSubmit={handleBindEmail} style={styles.passwordForm}>
                <input
                  type="email"
                  placeholder="请输入邮箱地址"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.inputFull}
                  disabled={emailLoading}
                />
                <button
                  type="submit"
                  style={styles.smallPrimaryButton}
                  disabled={emailLoading}
                >
                  {emailLoading ? '提交中...' : (user?.email ? '修改邮箱' : '绑定邮箱')}
                </button>
              </form>
            </div>

            {/* 用户ID */}
            <div style={styles.infoItem}>
              <label style={styles.label}>用户ID</label>
              <div style={styles.value}>#{user?.id || '-'}</div>
            </div>

            {/* 登录方式 */}
            <div style={styles.infoItem}>
              <label style={styles.label}>登录方式</label>
              <div style={styles.value}>
                {user?.openid ? '🐧 QQ登录' : '📧 邮箱登录'}
              </div>
            </div>

            <div style={styles.passwordBlock}>
              <div style={styles.passwordTitle}>
                {user?.hasPassword ? '修改账号密码登录密码' : '设置账号密码登录密码'}
              </div>
              <p style={styles.passwordHint}>
                {user?.hasPassword
                  ? '已设置密码，可直接用账号与密码登录。'
                  : '设置后可使用账号与密码直接登录。'}
              </p>
              <form onSubmit={handlePasswordSubmit} style={styles.passwordForm}>
                {shouldRequireCurrentPassword && (
                  <input
                    type="password"
                    placeholder="当前密码"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    style={styles.inputFull}
                    disabled={passwordLoading}
                  />
                )}
                <input
                  type="password"
                  placeholder="新密码（至少 6 位）"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={styles.inputFull}
                  disabled={passwordLoading}
                />
                <input
                  type="password"
                  placeholder="确认新密码"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={styles.inputFull}
                  disabled={passwordLoading}
                />
                <button
                  type="submit"
                  style={styles.smallPrimaryButton}
                  disabled={passwordLoading}
                >
                  {passwordLoading ? '提交中...' : (user?.hasPassword ? '修改密码' : '设置密码')}
                </button>
              </form>
            </div>
          </div>

          {/* 操作按钮 */}
          <div style={styles.buttonGroup}>
            {editing ? (
              <>
                <button 
                  onClick={handleCancel}
                  style={styles.secondaryButton}
                  disabled={loading}
                >
                  取消
                </button>
                <button 
                  onClick={handleSave}
                  style={styles.primaryButton}
                  disabled={loading}
                >
                  {loading ? '保存中...' : '保存修改'}
                </button>
              </>
            ) : (
              <>
                <button 
                  onClick={() => setEditing(true)}
                  style={styles.primaryButton}
                >
                  ✏️ 编辑资料
                </button>
                <button 
                  onClick={handleLogout}
                  style={styles.dangerButton}
                >
                  🚪 退出登录
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '90vh',
    background: 'linear-gradient(180deg, #1a1d23 0%, #282c34 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  contentLayer: {
    position: 'relative',
    zIndex: 10,
    padding: '40px 20px',
    display: 'flex',
    justifyContent: 'center',
  },
  card: {
    position: 'relative',
    width: '100%',
    maxWidth: '500px',
    padding: '40px',
    backgroundColor: 'rgba(37, 40, 48, 0.95)',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    WebkitBackdropFilter: 'blur(20px)',
    backdropFilter: 'blur(20px)',
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#9ca3af',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '24px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '16px',
    color: '#9ca3af',
    marginBottom: '24px',
    textAlign: 'center',
  },
  messageBox: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid',
    marginBottom: '20px',
    fontSize: '14px',
    textAlign: 'center',
  },
  avatarSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '32px',
  },
  avatarWrapper: {
    position: 'relative',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '4px solid #12B7F5',
    boxShadow: '0 8px 30px rgba(18, 183, 245, 0.3)',
  },
  avatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#3d4450',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#12B7F5',
  },
  avatarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '24px',
    gap: '4px',
  },
  avatarHint: {
    marginTop: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  infoSection: {
    marginBottom: '32px',
  },
  passwordBlock: {
    marginTop: '20px',
    padding: '16px',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  emailBlock: {
    marginTop: '20px',
    padding: '16px',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  passwordTitle: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '6px',
  },
  passwordHint: {
    color: '#9ca3af',
    fontSize: '12px',
    marginTop: 0,
    marginBottom: '12px',
  },
  passwordForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  label: {
    fontSize: '14px',
    color: '#9ca3af',
    fontWeight: '500',
  },
  value: {
    fontSize: '15px',
    color: '#ffffff',
    fontWeight: '500',
  },
  input: {
    width: '200px',
    padding: '10px 14px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '15px',
    outline: 'none',
    textAlign: 'right',
  },
  inputFull: {
    width: '100%',
    padding: '10px 14px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  smallPrimaryButton: {
    padding: '10px 14px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  primaryButton: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '10px',
    color: 'white',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  secondaryButton: {
    padding: '14px 28px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  dangerButton: {
    padding: '14px 28px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '10px',
    color: '#ef4444',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
};

export default Profile;

